// Eventos especiales de Disney que Jenny ha vivido: página /eventos, una página por evento y la sección de Inicio.
// Las fotos son las mismas de "Mi mundo" (images/<carpeta>/<archivo>); la página usa sus versiones web.

/**
 * Texto con una parte en cursiva (del color del evento), como los títulos del sitio.
 * @typedef {Object} TextoConEnfasis
 * @property {string} antes
 * @property {string} em
 * @property {string} [despues]
 */

/**
 * Colores de un evento.
 * @typedef {Object} TemaEvento
 * @property {boolean} oscuro       portada de noche (texto claro) o de día (texto oscuro)
 * @property {string} fondo         fondo de la portada y de su boleto
 * @property {string} fondo2        fondo de "No te lo pierdas" en los eventos de noche
 * @property {string} acento        color de la parte en cursiva del título y de los íconos sobre la portada
 * @property {string} subtitulo     color del subtítulo escrito a mano
 * @property {string} texto         texto sobre la portada
 * @property {string} suave         texto secundario sobre la portada
 * @property {string} banda         color de su pulsera
 * @property {string} broche        broche de la pulsera
 * @property {string} bandaTexto    letras de la pulsera
 * @property {string} claro         fondo claro de sus íconos sobre papel
 * @property {string} oscuroAcento  acento oscuro para texto sobre papel
 */

/**
 * Foto de un evento.
 * @typedef {Object} FotoEvento
 * @property {string} src          ruta de la foto original (images/…)
 * @property {string} pie
 * @property {'v'|'h'} forma       en su polaroid: vertical (4:5) u horizontal (4:3)
 * @property {string} [encuadre]   object-position del recorte
 */

/**
 * En qué pantallas se ve una foto de portada: en todas, solo en escritorio (900 px o más) o solo en el celular.
 * Las que no tocan no se dibujan, así el navegador no las descarga.
 * Tira "Más fotos" debajo del collage de "Lo que viví": polaroids que se deslizan de lado.
 * @typedef {Object} MasFotos
 * @property {string} titulo        título escrito a mano ("Más fotos de la fiesta")
 * @property {FotoEvento[]} fotos
 */

/**
 * @typedef {'todas'|'escritorio'|'movil'} VistaFoto
 */

/**
 * Polaroid de la portada de un evento.
 * @typedef {Object} FotoPortadaEvento
 * @property {string} src
 * @property {string} pie
 * @property {'v'|'h'} forma
 * @property {string} [encuadre]
 * @property {VistaFoto} vista
 */

/**
 * Foto a media portada con corte en diagonal (solo en escritorio).
 * @typedef {Object} FondoPortada
 * @property {string} src
 * @property {string} encuadre
 */

/**
 * Lo que no hay que perderse de un evento.
 * @typedef {Object} Imperdible
 * @property {string} icono   clases de Font Awesome
 * @property {string} nombre
 * @property {string} texto
 */

/**
 * @typedef {'oogie-boogie-bash'|'not-so-scary'|'food-and-wine'|'pixar-fest'|'very-merry'} IdEvento
 */

/**
 * Animación de la portada de cada evento: niebla y murciélagos, confeti de Halloween, pasaporte con sellos,
 * lámpara y globos, o nieve.
 * @typedef {'halloween-villanos'|'halloween-confeti'|'pasaporte'|'pixar'|'navidad'} AmbienteEvento
 */

/**
 * @typedef {Object} Evento
 * @property {IdEvento} id                  su página es /eventos/<id>
 * @property {AmbienteEvento} ambiente
 * @property {number} anio                  año en que Jenny fue
 * @property {TextoConEnfasis} titulo       nombre oficial completo (se usa en pulseras, boletos, calendario y listas)
 * @property {string} sub                   subtítulo escrito a mano
 * @property {string} parque
 * @property {string} lugar
 * @property {string} cuando
 * @property {string} temporadaCorta        etiqueta de su barra en el calendario
 * @property {[number, number]} rango       meses del calendario: enero = 0, con fracción de mes
 * @property {string} boleto
 * @property {string} lead
 * @property {TextoConEnfasis} viviTitulo
 * @property {string} viviTexto
 * @property {FotoEvento[]} fotos           "Lo que viví" (3 a 5)
 * @property {MasFotos} [masFotos]          tira de más fotos debajo del collage
 * @property {TextoConEnfasis} imperdiblesTitulo
 * @property {Imperdible[]} imperdibles
 * @property {string} tip                   Tip de Jenny
 * @property {TextoConEnfasis} ctaTitulo
 * @property {string} ctaTexto
 * @property {string} waTexto               mensaje de WhatsApp de su botón
 * @property {{ src: string, encuadre: string }} portada   foto de su boleto
 * @property {FotoPortadaEvento[]} fotosPortada   polaroids de su portada, en el orden de su acomodo
 * @property {FondoPortada} [fondoPortada]        foto en diagonal de su portada (escritorio)
 * @property {TemaEvento} tema
 */

const DW = 'images/disney-world-orlando/disney-world';
const DL = 'images/disneyland-california/disney';

/** Fotos de los eventos, por nombre. */
export const fotosEventos = {
  jack: 'images/disneyland-california/disney-jack-skellington.webp',
  caldero: `${DL}15.webp`,
  calabazaMickey: `${DL}11.webp`,
  confeti: `${DW}20.webp`,
  mainStreet: `${DW}18.webp`,
  varita: `${DW}22.webp`,
  castillo2026: `${DW}19.webp`,
  sarten: `${DW}23.webp`,
  spaceship: `${DW}6.webp`,
  journey: `${DW}12.webp`,
  china: `${DW}14.webp`,
  pixar: `${DL}3.webp`,
  pixarPier: `${DL}7.webp`,
  up: `${DL}4.webp`,
  carsLand: `${DL}8.webp`,
  pulsera: `${DW}1.webp`,
  enanos: `${DW}4.webp`,
  elsa: `${DW}2.webp`,
  castilloAzul: `${DW}3.webp`,
  castilloFuegos: `${DW}5.webp`,
  castilloHalloween: `${DW}25.webp`,
  agentes: `${DW}26.webp`,
  sobrinosDonald: `${DW}27.webp`,
  vueltaMainStreet: `${DW}28.webp`,
  companeras: `${DW}29.webp`,
  bruno: `${DL}24.webp`,
  haulOWeen: `${DL}25.webp`,
  mcqueen: `${DL}26.webp`,
  tinaDulces: `${DL}27.webp`,
  letreroPixarFest: `${DL}29.webp`,
  unidos: `${DL}30.webp`
};
const F = fotosEventos;

/** @type {Evento[]} */
export const eventos = [
  {
    id: 'oogie-boogie-bash', ambiente: 'halloween-villanos', anio: 2024,
    titulo: { antes: 'Oogie Boogie ', em: 'Bash' },
    sub: 'A Disney Halloween Party',
    parque: 'Disney California Adventure', lugar: 'Disney California Adventure, Anaheim',
    cuando: 'Noches seleccionadas de septiembre y octubre', temporadaCorta: 'sep – oct', rango: [8.1, 10],
    boleto: 'Aparte, con cupo limitado',
    lead: 'La fiesta de Halloween de Disney California Adventure: villanos, niebla verde, dulces y personajes de Halloween. Aquí conocí a Jack Skellington.',
    viviTitulo: { antes: 'Mi noche con ', em: 'Jack Skellington' },
    viviTexto: 'Recorrí el parque entre calabazas, luces de feria y el caldero verde que echa humo. Estas son mis fotos favoritas.',
    fotos: [
      { src: F.caldero, pie: 'Celebración de Halloween', forma: 'v' },
      { src: F.jack, pie: 'Encuentro con Jack Skellington', forma: 'h' },
      { src: F.calabazaMickey, pie: 'Festival de Calabazas en Main Street', forma: 'v' }
    ],
    masFotos: {
      titulo: 'Más fotos de la fiesta',
      fotos: [
        { src: F.haulOWeen, pie: 'Honkin’ Haul-O-Ween en Cars Land', forma: 'v' },
        { src: F.tinaDulces, pie: 'La tina de dulces de la fiesta', forma: 'v' },
        { src: F.bruno, pie: 'Encuentro con Bruno', forma: 'v' },
        { src: F.mcqueen, pie: 'Con el Rayo McQueen', forma: 'v' }
      ]
    },
    imperdiblesTitulo: { antes: 'Lo mejor de ', em: 'la noche' },
    imperdibles: [
      { icono: 'fa-solid fa-flag', nombre: 'Frightfully Fun Parade', texto: 'El desfile de la fiesta, con villanos y personajes de Halloween.' },
      { icono: 'fa-solid fa-droplet', nombre: 'World of Color – Villainous!', texto: 'El show de agua y luces de la noche, protagonizado por los villanos.' },
      { icono: 'fa-solid fa-candy-cane', nombre: 'Treat Trails', texto: 'Senderos de dulces por todo el parque. Lleva una bolsa.' },
      { icono: 'fa-solid fa-moon', nombre: 'Villains Grove', texto: 'Un jardín iluminado donde los villanos cobran vida.' }
    ],
    tip: 'Con el boleto de la fiesta puedes entrar al parque desde la tarde: aprovecha esas horas para las atracciones y deja la noche para la fiesta.',
    ctaTitulo: { antes: '¿Quieres vivir la ', em: 'Oogie Boogie Bash', despues: '?' },
    ctaTexto: 'Te ayudo con los boletos, el hotel y el plan de la noche.',
    waTexto: 'Hola Jenny, quiero ir a la Oogie Boogie Bash. ¿Me ayudas?',
    portada: { src: F.jack, encuadre: 'center 30%' },
    fotosPortada: [
      { src: F.caldero, pie: 'Celebración de Halloween', forma: 'v', vista: 'todas' },
      { src: F.jack, pie: 'Encuentro con Jack Skellington', forma: 'h', vista: 'todas' }
    ],
    tema: {
      oscuro: true, fondo: '#150E22', fondo2: '#211533', acento: '#9BE15D', subtitulo: '#B98CF2', texto: '#F4EFFB', suave: '#D3C7E8',
      banda: '#9BE15D', broche: '#150E22', bandaTexto: '#1B1230', claro: '#EDF9E2', oscuroAcento: '#3F7A1C'
    }
  },
  {
    id: 'not-so-scary', ambiente: 'halloween-confeti', anio: 2026,
    titulo: { antes: 'Mickey’s Not-So-Scary ', em: 'Halloween Party' },
    sub: 'Halloween en Magic Kingdom',
    parque: 'Magic Kingdom', lugar: 'Magic Kingdom, Walt Disney World',
    cuando: 'Noches seleccionadas de agosto a octubre', temporadaCorta: 'ago – oct', rango: [7.25, 10],
    boleto: 'Aparte, con cupo limitado',
    lead: 'Magic Kingdom de noche y decorado de Halloween, con desfile, fuegos artificiales y dulces en cada esquina. Aquí se vale ir disfrazado.',
    viviTitulo: { antes: 'Confeti, calabazas y ', em: 'el castillo' },
    viviTexto: 'Main Street se llena de calabazas y el Castillo de Cenicienta se vuelve el mejor fondo para las fotos.',
    fotos: [
      { src: F.confeti, pie: 'Halloween frente al castillo', forma: 'h' },
      { src: F.mainStreet, pie: 'La fiesta en Main Street', forma: 'v' },
      { src: F.varita, pie: 'Frente al Castillo de Cenicienta', forma: 'v' },
      { src: F.castillo2026, pie: 'Magic Kingdom 2026', forma: 'v' }
    ],
    masFotos: {
      titulo: 'Más fotos de la fiesta',
      fotos: [
        { src: F.agentes, pie: 'Con mis compañeras agentes de viajes', forma: 'h' },
        { src: F.companeras, pie: 'Listas para la fiesta', forma: 'h' },
        { src: F.castilloHalloween, pie: 'Magic Kingdom en Halloween', forma: 'v', encuadre: 'center 25%' },
        { src: F.vueltaMainStreet, pie: 'Main Street, U.S.A.', forma: 'v', encuadre: 'center 65%' },
        { src: F.sobrinosDonald, pie: 'Hugo, Paco y Luis en Main Street', forma: 'v', encuadre: 'center 70%' }
      ]
    },
    imperdiblesTitulo: { antes: 'Lo mejor de ', em: 'la noche' },
    imperdibles: [
      { icono: 'fa-solid fa-flag', nombre: 'Mickey’s Boo-to-You Halloween Parade', texto: 'El desfile de Halloween, abierto por el Jinete sin Cabeza.' },
      { icono: 'fa-solid fa-burst', nombre: 'Disney’s Not-So-Spooky Spectacular', texto: 'Los fuegos artificiales de Halloween sobre el castillo.' },
      { icono: 'fa-brands fa-fort-awesome', nombre: 'Hocus Pocus Villain Spelltacular', texto: 'Las hermanas Sanderson en el escenario del castillo.' },
      { icono: 'fa-solid fa-candy-cane', nombre: 'Dulces por todo el parque', texto: 'Estaciones de dulces repartidas en cada tierra del parque.' }
    ],
    tip: 'Lleva una bolsa grande para los dulces y ve al desfile en su segunda función: suele haber menos gente.',
    ctaTitulo: { antes: '¿Quieres vivir la ', em: 'Mickey’s Not-So-Scary Halloween Party', despues: '?' },
    ctaTexto: 'Te ayudo con los boletos, el hotel y el plan de la noche.',
    waTexto: 'Hola Jenny, quiero ir a la Mickey’s Not-So-Scary Halloween Party. ¿Me ayudas?',
    portada: { src: F.confeti, encuadre: 'center 32%' },
    fotosPortada: [
      { src: F.confeti, pie: 'Halloween frente al castillo', forma: 'h', encuadre: 'center 35%', vista: 'movil' }
    ],
    fondoPortada: { src: F.confeti, encuadre: '56% center' },
    tema: {
      oscuro: true, fondo: '#1C1330', fondo2: '#2B1B47', acento: '#F7931E', subtitulo: '#FFD166', texto: '#FFF7EC', suave: '#E4D8F0',
      banda: '#F7931E', broche: '#2B1B47', bandaTexto: '#2A1640', claro: '#FDEBD6', oscuroAcento: '#A8540A'
    }
  },
  {
    id: 'food-and-wine', ambiente: 'pasaporte', anio: 2026,
    titulo: { antes: 'EPCOT International ', em: 'Food & Wine', despues: ' Festival' },
    sub: 'Una vuelta al mundo en bocados',
    parque: 'EPCOT', lugar: 'EPCOT, Walt Disney World',
    cuando: 'De finales de agosto a noviembre', temporadaCorta: 'fin de ago – nov', rango: [7.9, 10.75],
    boleto: 'Incluido en tu entrada a EPCOT; la comida se paga aparte',
    lead: 'EPCOT se llena de puestos con comida y bebida de muchos países, conciertos y un pasaporte para ir marcando lo que pruebas.',
    viviTitulo: { antes: 'Mi sartén de ', em: 'Food & Wine' },
    viviTexto: 'El festival en 2026 y el EPCOT de siempre: Spaceship Earth, Journey of Water y los pabellones del mundo.',
    fotos: [
      { src: F.sarten, pie: 'Food & Wine Festival 2026', forma: 'v' },
      { src: F.spaceship, pie: 'Spaceship Earth', forma: 'v' },
      { src: F.journey, pie: 'Journey of Water, inspirado en Moana', forma: 'v' },
      { src: F.china, pie: 'Pabellón de China', forma: 'v' }
    ],
    imperdiblesTitulo: { antes: 'Lo mejor del ', em: 'festival' },
    imperdibles: [
      { icono: 'fa-solid fa-earth-americas', nombre: 'Global Marketplaces', texto: 'Puestos con platillos y bebidas de todo el mundo, en porciones para probar.' },
      { icono: 'fa-solid fa-music', nombre: 'Eat to the Beat', texto: 'Conciertos en vivo en el America Gardens Theatre.' },
      { icono: 'fa-solid fa-passport', nombre: 'Festival Passport', texto: 'El pasaporte con el menú de cada puesto, para ir marcando lo que pruebas.' },
      { icono: 'fa-solid fa-magnifying-glass', nombre: 'Remy’s Ratatouille Hide & Squeak', texto: 'Una búsqueda por el parque para los niños.' }
    ],
    tip: 'Comparte los platillos: las porciones son pequeñas y así pruebas más países. Entre semana hay menos fila.',
    ctaTitulo: { antes: '¿Quieres vivir el ', em: 'EPCOT International Food & Wine Festival', despues: '?' },
    ctaTexto: 'Te ayudo con los boletos, el hotel y a planear tus días en EPCOT.',
    waTexto: 'Hola Jenny, quiero ir al EPCOT International Food & Wine Festival. ¿Me ayudas?',
    portada: { src: F.sarten, encuadre: 'center 30%' },
    fotosPortada: [
      { src: F.sarten, pie: 'Food & Wine Festival 2026', forma: 'v', vista: 'todas' }
    ],
    tema: {
      oscuro: false, fondo: '#F4ECDD', fondo2: '#EADFCB', acento: '#7B2335', subtitulo: '#3F5E3A', texto: '#13212B', suave: '#4A5961',
      banda: '#7B2335', broche: '#E9C46A', bandaTexto: '#FFF4E4', claro: '#F6E3E6', oscuroAcento: '#7B2335'
    }
  },
  {
    id: 'pixar-fest', ambiente: 'pixar', anio: 2024,
    titulo: { antes: 'Pixar ', em: 'Fest' },
    sub: 'Disneyland celebra a Pixar',
    parque: 'Disneyland Resort', lugar: 'Disneyland Resort, Anaheim',
    cuando: 'Del 26 de abril al 4 de agosto de 2024', temporadaCorta: '26 abr – 4 ago · 2024', rango: [3.83, 7.13],
    boleto: 'Incluido en tu entrada a Disneyland',
    lead: 'Disneyland celebró a Pixar con desfile, fuegos artificiales y sus personajes por todo el resort. Aquí conocí a Russell y a Dug de Up.',
    viviTitulo: { antes: 'Color, globos y ', em: 'mucha nostalgia' },
    viviTexto: 'Del letrero de Pixar a Pixar Pier y Cars Land: así se vivió el festival.',
    fotos: [
      { src: F.pixar, pie: 'Pixar', forma: 'h' },
      { src: F.up, pie: 'Encuentro con Russell y Dug', forma: 'v' },
      { src: F.pixarPier, pie: 'Pixar Pier', forma: 'v' },
      { src: F.carsLand, pie: 'Creando recuerdos en Cars Land', forma: 'h' }
    ],
    masFotos: {
      titulo: 'Más fotos del festival',
      fotos: [
        { src: F.letreroPixarFest, pie: 'Pixar Fest 2024', forma: 'v', encuadre: 'center 60%' },
        { src: F.unidos, pie: 'Con Ian y Barley de Unidos', forma: 'h' }
      ]
    },
    imperdiblesTitulo: { antes: 'Lo mejor del ', em: 'festival' },
    imperdibles: [
      { icono: 'fa-solid fa-flag', nombre: 'Better Together: A Pixar Pals Celebration!', texto: 'El desfile de Pixar Fest, con personajes de muchas películas.' },
      { icono: 'fa-solid fa-burst', nombre: 'Together Forever', texto: 'Los fuegos artificiales nocturnos con música de Pixar.' },
      { icono: 'fa-solid fa-droplet', nombre: 'World of Color – Happiness!', texto: 'El show de agua y luces en California Adventure.' },
      { icono: 'fa-solid fa-wand-magic-sparkles', nombre: 'Pixar Pier', texto: 'La zona de Pixar, con la Incredicoaster y la rueda de la fortuna.' }
    ],
    tip: 'Aparta tu lugar para los fuegos artificiales con tiempo: Main Street se llena rápido.',
    ctaTitulo: { antes: '¿Quieres vivir la magia de ', em: 'Pixar', despues: '?' },
    ctaTexto: 'Pixar Fest fue una edición especial de 2024, pero Pixar Pier, Cars Land y sus personajes siguen ahí. Te ayudo a planear tu viaje.',
    waTexto: 'Hola Jenny, quiero un viaje a Disneyland con experiencias de Pixar. ¿Me ayudas?',
    portada: { src: F.pixar, encuadre: 'center 45%' },
    fotosPortada: [
      { src: F.letreroPixarFest, pie: 'Pixar Fest 2024', forma: 'v', encuadre: 'center 60%', vista: 'todas' },
      { src: F.up, pie: 'Con Russell y Dug', forma: 'v', vista: 'escritorio' }
    ],
    tema: {
      oscuro: false, fondo: '#FFF7EA', fondo2: '#FCEBD2', acento: '#E63946', subtitulo: '#2F6FDB', texto: '#13212B', suave: '#4A5961',
      banda: '#2F6FDB', broche: '#FFC43D', bandaTexto: '#FFFFFF', claro: '#E6EEFB', oscuroAcento: '#1F56B8'
    }
  },
  {
    id: 'very-merry', ambiente: 'navidad', anio: 2023,
    titulo: { antes: 'Mickey’s Very Merry ', em: 'Christmas Party' },
    sub: 'La Navidad en Magic Kingdom',
    parque: 'Magic Kingdom', lugar: 'Magic Kingdom, Walt Disney World',
    cuando: 'Noches seleccionadas de noviembre y diciembre', temporadaCorta: 'nov – dic', rango: [10.2, 11.65],
    boleto: 'Aparte, con cupo limitado',
    lead: 'La Navidad de Magic Kingdom: nieve en Main Street, galletas y chocolate caliente, desfile navideño y el castillo iluminado.',
    viviTitulo: { antes: 'Luces, galletas y ', em: 'un castillo de cuento' },
    viviTexto: 'Desde la pulsera de la fiesta hasta los fuegos artificiales sobre el castillo iluminado.',
    fotos: [
      { src: F.pulsera, pie: 'Lista para la fiesta', forma: 'v' },
      { src: F.castilloAzul, pie: 'El castillo iluminado', forma: 'v', encuadre: 'center 20%' },
      { src: F.enanos, pie: 'Con los siete enanos', forma: 'h' },
      { src: F.elsa, pie: 'Show de Elsa frente al castillo', forma: 'v' },
      { src: F.castilloFuegos, pie: 'Fuegos artificiales sobre el castillo', forma: 'v', encuadre: 'center 25%' }
    ],
    imperdiblesTitulo: { antes: 'Lo mejor de ', em: 'la noche' },
    imperdibles: [
      { icono: 'fa-solid fa-flag', nombre: 'Mickey’s Once Upon a Christmastime Parade', texto: 'El desfile navideño, con Santa Claus al final.' },
      { icono: 'fa-solid fa-burst', nombre: 'Minnie’s Wonderful Christmastime Fireworks', texto: 'Los fuegos artificiales de Navidad sobre el castillo.' },
      { icono: 'fa-solid fa-mug-hot', nombre: 'Galletas y chocolate caliente', texto: 'Incluidos con tu boleto, en estaciones por todo el parque.' },
      { icono: 'fa-solid fa-snowflake', nombre: 'Nieve en Main Street', texto: 'Sí: después del desfile, nieva en Main Street, U.S.A.' }
    ],
    tip: 'Entra desde la tarde con el boleto de la fiesta y no te vayas sin ver la nieve en Main Street después del desfile.',
    ctaTitulo: { antes: '¿Quieres vivir la ', em: 'Mickey’s Very Merry Christmas Party', despues: '?' },
    ctaTexto: 'Te ayudo con los boletos, el hotel y el plan de la noche.',
    waTexto: 'Hola Jenny, quiero ir a la Mickey’s Very Merry Christmas Party. ¿Me ayudas?',
    portada: { src: F.castilloAzul, encuadre: 'center 28%' },
    fotosPortada: [
      { src: F.castilloAzul, pie: 'El castillo iluminado', forma: 'v', encuadre: 'center 20%', vista: 'movil' },
      { src: F.castilloFuegos, pie: 'Fuegos sobre el castillo', forma: 'v', encuadre: 'center 25%', vista: 'movil' }
    ],
    fondoPortada: { src: F.castilloAzul, encuadre: 'center 25%' },
    tema: {
      oscuro: true, fondo: '#0B1730', fondo2: '#12244A', acento: '#F2C14E', subtitulo: '#FF8A95', texto: '#F4F7FB', suave: '#C9D6EA',
      banda: '#1E7A56', broche: '#E0293B', bandaTexto: '#FFF8E6', claro: '#FBF1D9', oscuroAcento: '#8A6A12'
    }
  }
];

/** Orden del calendario: de la primavera a la Navidad. @type {IdEvento[]} */
export const ordenCalendario = ['pixar-fest', 'not-so-scary', 'food-and-wine', 'oogie-boogie-bash', 'very-merry'];

/**
 * Tipo de viajero de "¿Cuál es para ti?".
 * @typedef {Object} TipoViajero
 * @property {string} icono     clases de Font Awesome
 * @property {string} titulo
 * @property {string} texto
 * @property {IdEvento[]} eventos
 */

/** @type {TipoViajero[]} */
export const tiposViajero = [
  { icono: 'fa-solid fa-children', titulo: 'Con niños', texto: 'Disfraces, dulces, galletas y desfiles pensados para toda la familia.', eventos: ['not-so-scary', 'very-merry'] },
  { icono: 'fa-solid fa-utensils', titulo: 'Para foodies', texto: 'Una vuelta al mundo en bocados, sin salir de EPCOT.', eventos: ['food-and-wine'] },
  { icono: 'fa-solid fa-star', titulo: 'Fans de Pixar', texto: 'Desfile, fuegos artificiales y los personajes de sus películas.', eventos: ['pixar-fest'] },
  { icono: 'fa-solid fa-moon', titulo: 'Fans de los villanos', texto: 'La noche en que los villanos toman California Adventure.', eventos: ['oogie-boogie-bash'] }
];

/**
 * Polaroid de las portadas de la página Eventos y de la sección de Inicio: lleva a la página de su evento.
 * @typedef {Object} FotoPortada
 * @property {string} src
 * @property {string} pie
 * @property {'v'|'h'} forma
 * @property {string} [encuadre]
 * @property {VistaFoto} vista
 * @property {IdEvento} evento
 */

/** Página Eventos: textos y las 3 fotos de su portada. */
export const paginaEventos = {
  titulo: { antes: 'Las noches más mágicas de Disney ', em: 'que he vivido' },
  lead: 'Fiestas de Halloween y de Navidad, festivales y celebraciones especiales. Estuve en cada una: te cuento qué esperar y te ayudo a vivir la tuya.',
  calendario: 'Las fiestas son en noches seleccionadas y se agotan: conviene planear con meses de anticipación.',
  /** @type {FotoPortada[]} */
  fotos: [
    { src: F.castilloFuegos, pie: 'Very Merry 2023', forma: 'v', encuadre: 'center 25%', vista: 'todas', evento: 'very-merry' },
    { src: F.confeti, pie: 'Not So Scary 2026', forma: 'h', vista: 'todas', evento: 'not-so-scary' },
    { src: F.pixar, pie: 'Pixar Fest 2024', forma: 'h', vista: 'escritorio', evento: 'pixar-fest' }
  ],
  waTexto: 'Hola Jenny, quiero vivir uno de los eventos especiales de Disney. ¿Me ayudas?'
};

/** Sección de Inicio: textos y sus 2 fotos. */
export const seccionEventosInicio = {
  titulo: { antes: 'Noches especiales ', em: 'que ya viví' },
  lead: 'Fiestas de Halloween y de Navidad, el EPCOT International Food & Wine Festival y Pixar Fest: mira cómo se viven y descubre cuál va contigo.',
  /** @type {FotoPortada[]} */
  fotos: [
    { src: F.castilloAzul, pie: 'Very Merry 2023', forma: 'v', encuadre: 'center 20%', vista: 'todas', evento: 'very-merry' },
    { src: F.jack, pie: 'Oogie Boogie Bash 2024', forma: 'h', vista: 'todas', evento: 'oogie-boogie-bash' }
  ]
};
